import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from '@formspree/react';
import { useScreenInit } from '../useScreenInit.js';
import { CheckIcon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { ContactLinks } from '../components/contact/ContactLinks';
import { hasAnyContact } from '../data/contact';
import { useLanguage } from '../components/LanguageContext';
function MinimalHeader() {
  const {
    t
  } = useLanguage();
  return <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-id="element-8840" data-mp="35rtoc6rp70d">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-8841" data-mp="3vavdt6nklcz6">
        <Link to="/" className="flex items-center gap-3" aria-label={t('BCITI and ATNA — back to home', 'BCITI et ATNA — retour à l’accueil')} data-id="element-8842" data-mp="2nx1pfc0v4d8g">
          <span className="flex items-center" data-id="element-8843" data-mp="29hj6sq55uq88">
            <span className="font-sans text-2xl font-bold text-black tracking-[0.2em] leading-none" data-id="element-8844" data-mp="29iuakpylvxzc">
              BCITI
            </span>
            <span className="ml-2 inline-block w-3.5 h-3.5 bg-indigo" aria-hidden="true" data-id="element-8845" data-mp="2ee9n9ekvnnu6" />
          </span>
          <span className="h-6 w-px bg-black/20" aria-hidden="true" data-id="element-8846" data-mp="24hes95ntc5kn" />
          <span className="relative font-sans text-base font-medium text-black tracking-[0.25em] leading-none" data-id="element-8847" data-mp="1qrb8qmgbipyw">
            ATNA
          </span>
        </Link>
        <Link to="/" className="font-sans text-sm text-black/60 hover:text-black transition-colors" data-id="element-8848" data-mp="1vj2rwbx59tf0">
          {t('Back to home', 'Retour à l’accueil')}
        </Link>
      </div>
    </header>;
}
const fade = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -20
  },
  transition: {
    duration: 0.4
  }
};
export function RequestDemo() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const {
    t
  } = useLanguage();
  // Formspree — endpoint https://formspree.io/f/xaqgzlbg
  const [state, handleSubmit] = useForm('xaqgzlbg');
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    organization: '',
    orgType: ''
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // `industry` is passed by the light industry pages' "Talk to us" CTAs;
    // `vertical` is passed by ATNA assistant links. Both map to orgType.
    const industry = params.get('industry');
    const vertical = params.get('vertical');
    const typeMap: Record<string, string> = {
      // industry param values
      cities: 'cities',
      airports: 'airports',
      telecom: 'telcos',
      health: 'health',
      universities: 'universities',
      // vertical param values
      city: 'cities',
      airport: 'airports',
      telco: 'telcos',
      university: 'universities',
      brossard: 'cities'
    };
    const key = industry || vertical;
    if (key && typeMap[key]) {
      setForm(prev => ({
        ...prev,
        orgType: typeMap[key]
      }));
    }
  }, [location.search]);
  // Track a successful submission once Formspree confirms it.
  useEffect(() => {
    if (!state.succeeded) return;
    const params = new URLSearchParams(location.search);
    const source = params.get('source') || 'direct';
    trackEvent('demo_submitted', {
      source,
      vertical: form.orgType || 'unknown'
    });
  }, [state.succeeded]);
  // Masqué pour le moment : l'étape de prise de rendez-vous après un contact.
  // Le code de cette étape est conservé plus bas — passez ce flag à true pour le réactiver.
  const SHOW_BOOKING_STEP = false;
  // Screens preview: allow forcing the confirmation step via screen state.
  const submitted = state.succeeded || screenInit?.step === 1;
  // Étape RDV affichée uniquement si elle n'est pas masquée.
  const showConfirmation = submitted && SHOW_BOOKING_STEP;
  // Message court affiché à la place de l'étape RDV quand celle-ci est masquée.
  const showThanks = submitted && !SHOW_BOOKING_STEP;
  // Messages d'erreur bilingues (remplacent les messages anglais renvoyés par Formspree).
  const errorMessage = (code: string) => {
    switch (code) {
      case 'TYPE_EMAIL':
        return t('Please enter a valid email address.', 'Veuillez entrer une adresse courriel valide.');
      case 'REQUIRED_FIELD_EMPTY':
      case 'REQUIRED_FIELD_MISSING':
        return t('This field is required.', 'Ce champ est obligatoire.');
      default:
        return t('An error occurred while sending the form. Please try again.', 'Une erreur est survenue lors de l’envoi du formulaire. Veuillez réessayer.');
    }
  };
  const emailErrors = state.errors ? state.errors.getFieldErrors('email') : [];
  const formErrors = state.errors ? state.errors.getFormErrors() : [];
  const formValid = form.firstName.trim() && form.email.trim() && form.organization.trim();
  return <div className="w-full min-h-screen bg-white" data-id="element-8849" data-mp="3vvqs72ebani6">
      <MinimalHeader data-id="element-8850" data-mp="4ixk6zj9gfs7" />

      <main className="pt-32 pb-24 px-6 flex flex-col items-center justify-center min-h-screen" data-id="element-8851" data-mp="4jfkc791ne5e">
        <div className="w-full max-w-5xl mx-auto" data-id="element-8852" data-mp="3mxew4kuoexic">
          <AnimatePresence mode="wait" data-id="element-8853" data-mp="3qxyozc87veyo">
            {!submitted && <motion.div key="step0" {...fade} className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-8854" data-mp="3qy8fd0x6bo4w">
                {/* Left side — Value Prop */}
                <div className="pt-4" data-id="element-8855" data-mp="9hh30r2k5bgc">
                  <h1 className="font-serif text-4xl md:text-5xl font-normal text-black leading-tight mb-4" data-id="element-8856" data-mp="9gflc3r1dqpn">
                    {t('Request your demo', 'Demandez votre démo')}
                  </h1>
                  <p className="font-sans text-lg text-black/60 mb-12" data-id="element-8857" data-mp="2u6ws9hm4os3v">
                    {t('Discover how BCITI and ATNA can help your organization.', 'Découvrez comment BCITI et ATNA peuvent aider votre organisation.')}
                  </p>
                  <ul className="space-y-5" data-id="element-8858" data-mp="zs12iz8ytiw8">
                    {[t('City of Brossard case study', 'Étude de cas · Ville de Brossard'), t('400,000+ verified identities', 'Plus de 400 000 identités vérifiées'), t('20+ live communities', 'Plus de 20 communautés en production'), t('In production since 2017', 'En production depuis 2017'), t('AI-powered with ATNA', 'Propulsé par l’IA ATNA')].map(bullet => <li key={bullet} className="flex items-center gap-3" data-id="element-8859" data-mp="3kbzb7odxm1qa">
                        <CheckIcon className="w-5 h-5 text-indigo shrink-0" strokeWidth={2} data-id="element-8860" data-mp="3k9v8vavolas4" />
                        <span className="font-sans text-base text-black/80" data-id="element-8861" data-mp="3u32aqlr7q8ng">
                          {bullet}
                        </span>
                      </li>)}
                  </ul>
                  {hasAnyContact() && <div className="mt-12 pt-8 border-t border-black/10" data-id="element-8862" data-mp="36c6pk7h9ibp6">
                      <p className="font-sans text-base font-medium text-black mb-4" data-id="element-8863" data-mp="36eapkec1iqk6">
                        {t('Prefer to talk now?', 'Vous préférez en parler maintenant ?')}
                      </p>
                      <ContactLinks location="request_demo" data-id="element-8864" data-mp="3fas2nn3ysyik" />
                    </div>}
                </div>

                {/* Right side — Form */}
                <div className="bg-white border border-black/10 rounded-2xl p-8 md:p-10 shadow-sm" data-id="element-8865" data-mp="ulcj9d72sc2y">
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8866" data-mp="6alkfshrk11x">
                    <div data-id="element-8867" data-mp="1bs5rwqqq406">
                      <label htmlFor="firstName" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-8868" data-mp="1ck2jaw2q7s1">
                        {t('First name *', 'Prénom *')}
                      </label>
                      <input id="firstName" name="firstName" type="text" required value={form.firstName} onChange={e => setForm({
                    ...form,
                    firstName: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-8869" data-mp="ozs69odut8ry" />
                    </div>
                    <div data-id="element-8870" data-mp="2pnmp79j2gqp2">
                      <label htmlFor="email" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-8871" data-mp="2po656kp5p50g">
                        {t('Work email *', 'Courriel professionnel *')}
                      </label>
                      <input id="email" name="email" type="email" required value={form.email} onChange={e => setForm({
                    ...form,
                    email: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-8872" data-mp="21ybzcn398gpp" />
                      {emailErrors.length > 0 && <div className="mt-2 font-sans text-sm text-red-600" role="alert" data-id="element-8873" data-mp="31s3ja97lbf4y">
                          {emailErrors.map((error, index) => <p key={`${error.code}-${index}`} data-id="element-8874" data-mp="2s07jmtpuvgym" data-mpi={index}>
                              {t('Email', 'Courriel')} — {errorMessage(error.code)}
                            </p>)}
                        </div>}
                    </div>
                    <div data-id="element-8875" data-mp="36q59rf7cigqg">
                      <label htmlFor="organization" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-8876" data-mp="36otykubkm6pb">
                        {t('Organization *', 'Organisation *')}
                      </label>
                      <input id="organization" name="organization" type="text" required value={form.organization} onChange={e => setForm({
                    ...form,
                    organization: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-8877" data-mp="20a32r51b1c25" />
                    </div>
                    <div data-id="element-8878" data-mp="1it5ty8k8zhol">
                      <label htmlFor="orgType" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-8879" data-mp="1itfcxc6vkoks">
                        {t('Organization type', 'Type d’organisation')}{' '}
                        <span className="text-black/40" data-id="element-8880" data-mp="lbp1zjkgz9wb">
                          {t('(Optional)', '(Facultatif)')}
                        </span>
                      </label>
                      <select id="orgType" name="orgType" value={form.orgType} onChange={e => setForm({
                    ...form,
                    orgType: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors bg-white appearance-none" data-id="element-8881" data-mp="2qdt0j43pjpx5">
                        <option value="" disabled data-id="element-8882" data-mp="2ggy8n41xwnru">
                          {t('Select an option', 'Choisissez une option')}
                        </option>
                        <option value="cities" data-id="element-8883" data-mp="3rv2125svq3us">
                          {t('Cities & government', 'Villes et gouvernement')}
                        </option>
                        <option value="airports" data-id="element-8884" data-mp="5st8ln7kneaa">
                          {t('Airports & transportation', 'Aéroports et transport')}
                        </option>
                        <option value="telcos" data-id="element-8885" data-mp="3o83v6kl2h5nu">
                          {t('Telcos & media', 'Télécoms et médias')}
                        </option>
                        <option value="health" data-id="element-8886" data-mp="3j9skk7my6r4k">
                          {t('Health networks', 'Réseaux de santé')}
                        </option>
                        <option value="universities" data-id="element-8887" data-mp="399rwy9i1pouk">
                          {t('Universities', 'Universités')}
                        </option>
                        <option value="other" data-id="element-8888" data-mp="35aj2t3j3foyh">{t('Other', 'Autre')}</option>
                      </select>
                    </div>

                    {formErrors.length > 0 && <div className="font-sans text-sm text-red-600" role="alert" data-id="element-8889" data-mp="13io3zohxrexo">
                        {formErrors.map((error, index) => <p key={`${error.code}-${index}`} data-id="element-8890" data-mp="1fjmeb79qpha8" data-mpi={index}>{errorMessage(error.code)}</p>)}
                      </div>}

                    <div className="pt-2" data-id="element-8891" data-mp="11jlgy1soorzi">
                      <button type="submit" disabled={!formValid || state.submitting} className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-black text-white text-base font-medium rounded-xl hover:bg-black/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-8892" data-mp="15l6rvhlnqw72">
                        {state.submitting ? t('Sending…', 'Envoi…') : t('Book my demo', 'Réserver ma démo')}
                        {!state.submitting && <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8893" data-mp="du0tb5ll56jw" />}
                      </button>
                      <div className="mt-4 text-center space-y-2" data-id="element-8894" data-mp="3qeo6p6l6oknm">
                        <p className="font-sans text-sm text-black/50" data-id="element-8895" data-mp="3lfbj2jmhn9jz">
                          {t('Takes less than 30 seconds.', 'Moins de 30 secondes.')}
                        </p>
                        <p className="font-sans text-xs text-black/40" data-id="element-8896" data-mp="3vfc4qc7ty1rc">
                          {t('By submitting this form, you agree that BCITI may contact you regarding your demo request.', 'En soumettant ce formulaire, vous acceptez que BCITI vous contacte au sujet de votre demande de démo.')}
                        </p>
                      </div>
                    </div>
                  </form>
                </div>
              </motion.div>}

            {/* Message court affiché après l'envoi — l'étape de prise de RDV est masquée pour le moment. */}
            {showThanks && <motion.div key="thanks" {...fade} className="max-w-2xl mx-auto text-center" data-id="element-8897" data-mp="3komw8hi8b3d4">
                <div className="w-16 h-16 rounded-full bg-indigo/10 flex items-center justify-center mx-auto mb-8" data-id="element-8898" data-mp="3tqcv28efx4kx">
                  <CheckIcon className="w-8 h-8 text-indigo" strokeWidth={2} data-id="element-8899" data-mp="2eb6yj3mj65e" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-normal text-black mb-4" data-id="element-8900" data-mp="7bbbgpkts0ue">
                  {t('Thank you!', 'Merci !')}
                </h2>
                <p className="font-sans text-lg text-black/60 mb-12" data-id="element-8901" data-mp="h9hg3ey0nl2y">
                  {t('We received your request and will get back to you shortly.', 'Nous avons bien reçu votre demande et vous répondrons sous peu.')}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 pt-8 border-t border-black/10" data-id="element-8902" data-mp="2n2fs18pu7d1a">
                  <Link to="/" className="font-sans text-sm font-medium text-black/60 hover:text-black transition-colors" data-id="element-8903" data-mp="2fkhtu0d3vodd">
                    {t('Back to home', 'Retour à l’accueil')}
                  </Link>
                  <span className="text-black/20" data-id="element-8904" data-mp="21sbgsn591xi5">•</span>
                  <Link to="/brossard" className="font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-8905" data-mp="21twakajqpcey">
                    {t('Explore Brossard', 'Explorer Brossard')}
                  </Link>
                </div>
              </motion.div>}

            {showConfirmation && <motion.div key="step1" {...fade} className="max-w-4xl mx-auto text-center" data-id="element-8906" data-mp="2khrihdso7w05">
                <div className="w-16 h-16 rounded-full bg-indigo/10 flex items-center justify-center mx-auto mb-8" data-id="element-8907" data-mp="193noi7m327v3">
                  <CalendarIcon className="w-8 h-8 text-indigo" strokeWidth={2} data-id="element-8908" data-mp="3953xzc0nkh4c" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-normal text-black mb-4" data-id="element-8909" data-mp="3d4w0p83d7t1s">
                  {t('You’re booked.', 'C’est réservé.')}
                </h2>
                <p className="font-sans text-lg text-black/60 mb-12" data-id="element-8910" data-mp="3472hmyum2vwk">
                  {t('Choose a convenient time.', 'Choisissez un moment qui vous convient.')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start text-left" data-id="element-8911" data-mp="3i6le2ue28it1">
                  {/* Fake Calendar Embed Area */}
                  <div className="bg-black/[0.02] border border-black/10 rounded-2xl p-8 min-h-[400px] flex flex-col items-center justify-center text-center" data-id="element-8912" data-mp="3n43mmjo60w0i">
                    <CalendarIcon className="w-10 h-10 text-black/20 mb-4" strokeWidth={1.5} data-id="element-8913" data-mp="jg1ss49vd64b" />
                    <div className="font-sans text-base font-medium text-black/60 mb-2" data-id="element-8914" data-mp="244vyc5xsvh2b">
                      {t('Calendar scheduling area', 'Zone de prise de rendez-vous')}
                    </div>
                    <div className="font-sans text-sm text-black/40" data-id="element-8915" data-mp="28yqg33tuhlyr">
                      {t('(Embed code would load here)', '(Le code d’intégration se chargerait ici)')}
                    </div>
                  </div>

                  {/* What you will see */}
                  <div className="pt-4" data-id="element-8916" data-mp="1u66j6kxagapr">
                    <div className="font-sans text-sm font-semibold tracking-[0.2em] text-black/45 mb-6" data-id="element-8917" data-mp="1p96k4tyuw885">
                      {t('WHAT YOU WILL SEE DURING YOUR DEMO:', 'CE QUE VOUS VERREZ PENDANT VOTRE DÉMO :')}
                    </div>
                    <ul className="space-y-4 mb-12" data-id="element-8918" data-mp="2qg0l3e5lfiy8">
                      {[t('Real customer example (City of Brossard)', 'Exemple client réel (Ville de Brossard)'), t('Infrastructure overview', 'Vue d’ensemble de l’infrastructure'), t('Communications platform', 'Plateforme de communications'), t('Resident services', 'Services aux citoyens'), t('ATNA AI capabilities', 'Capacités d’IA ATNA'), t('Recommended deployment path', 'Parcours de déploiement recommandé')].map(item => <li key={item} className="flex items-center gap-3" data-id="element-8919" data-mp="ale0dn9hj5h3">
                          <CheckIcon className="w-5 h-5 text-indigo shrink-0" strokeWidth={2} data-id="element-8920" data-mp="fl0ahnu0o5lo" />
                          <span className="font-sans text-base text-black/80" data-id="element-8921" data-mp="1nbr6kfll6iq">
                            {item}
                          </span>
                        </li>)}
                    </ul>

                    <div className="flex flex-wrap items-center gap-4 pt-8 border-t border-black/10" data-id="element-8922" data-mp="3srijnlbrkk3g">
                      <Link to="/" className="font-sans text-sm font-medium text-black/60 hover:text-black transition-colors" data-id="element-8923" data-mp="3f0dr136tcs2m">
                        {t('Back to home', 'Retour à l’accueil')}
                      </Link>
                      <span className="text-black/20" data-id="element-8924" data-mp="3a113shc15izi">•</span>
                      <Link to="/brossard" className="font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-8925" data-mp="3a0hjitywqiib">
                        {t('Explore Brossard', 'Explorer Brossard')}
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>
    </div>;
}